import axios from 'axios';
import { addLocaleData } from 'react-intl';
import arLocaleData from 'react-intl/locale-data/ar';
import azLocaleData from 'react-intl/locale-data/az';
import bsLocaleData from 'react-intl/locale-data/bs';
import caLocaleData from 'react-intl/locale-data/ca';
import csLocaleData from 'react-intl/locale-data/cs';
import daLocaleData from 'react-intl/locale-data/da';
import deLocaleData from 'react-intl/locale-data/de';
import elLocaleData from 'react-intl/locale-data/el';
import enLocaleData from 'react-intl/locale-data/en';
import esLocaleData from 'react-intl/locale-data/es';
import euLocaleData from 'react-intl/locale-data/eu';
import fiLocaleData from 'react-intl/locale-data/fi';
import frLocaleData from 'react-intl/locale-data/fr';
import hrLocaleData from 'react-intl/locale-data/hr';
import huLocaleData from 'react-intl/locale-data/hu';
import itLocaleData from 'react-intl/locale-data/it';
import jaLocaleData from 'react-intl/locale-data/ja';
import ltLocaleData from 'react-intl/locale-data/lt';
import mkLocaleData from 'react-intl/locale-data/mk';
import nlLocaleData from 'react-intl/locale-data/nl';
import noLocaleData from 'react-intl/locale-data/no';
import plLocaleData from 'react-intl/locale-data/pl';
import ptLocaleData from 'react-intl/locale-data/pt';
import roLocaleData from 'react-intl/locale-data/ro';
import ruLocaleData from 'react-intl/locale-data/ru';
import skLocaleData from 'react-intl/locale-data/sk';
import slLocaleData from 'react-intl/locale-data/sl';
import sqLocaleData from 'react-intl/locale-data/sq';
import srLocaleData from 'react-intl/locale-data/sr';
import trLocaleData from 'react-intl/locale-data/tr';
import ukLocaleData from 'react-intl/locale-data/uk';
import zhLocaleData from 'react-intl/locale-data/zh';

import caTranslationMessages from './translations/ca.json';
import deTranslationMessages from './translations/de.json';
import elTranslationMessages from './translations/el.json';
import enTranslationMessages from './translations/en.json';
import esTranslationMessages from './translations/es.json';
import euTranslationMessages from './translations/eu.json';
import fiTranslationMessages from './translations/fi.json';
import frTranslationMessages from './translations/fr.json';
import itTranslationMessages from './translations/it.json';
import ptTranslationMessages from './translations/pt.json';
import nlTranslationMessages from './translations/nl.json';

addLocaleData(enLocaleData);
addLocaleData(frLocaleData);
addLocaleData(deLocaleData);
addLocaleData(itLocaleData);
addLocaleData(esLocaleData);
addLocaleData(caLocaleData);
addLocaleData(ptLocaleData);
addLocaleData(euLocaleData);
addLocaleData(nlLocaleData);
addLocaleData(plLocaleData);
addLocaleData(sqLocaleData);
addLocaleData(bsLocaleData);
addLocaleData(hrLocaleData);
addLocaleData(csLocaleData);
addLocaleData(fiLocaleData);
addLocaleData(huLocaleData);
addLocaleData(ltLocaleData);
addLocaleData(noLocaleData);
addLocaleData(roLocaleData);
addLocaleData(ruLocaleData);
addLocaleData(skLocaleData);
addLocaleData(slLocaleData);
addLocaleData(trLocaleData);
addLocaleData(ukLocaleData);
addLocaleData(mkLocaleData);
addLocaleData(jaLocaleData);
addLocaleData(arLocaleData);
addLocaleData(daLocaleData);
addLocaleData(azLocaleData);
addLocaleData(zhLocaleData);
addLocaleData(srLocaleData);
addLocaleData(elLocaleData);

// Use axios to asynchronously load the overrides
const loadLocaleOverride = (translations, locale, mergedFormattedMessages) => {
  if (translations && Object.prototype.hasOwnProperty.call(translations, locale)) {
    axios
      .get(translations[locale])
      .then(response => {
        if (response.status === 200) {
          return response.data;
        }
        return {};
      })
      .then(json => {
        Object.assign(mergedFormattedMessages, json);
      })
      .catch();
  }
};

// For a locale, ensure that we use the override if exist, or the default value in the language
// and if it doesn't exist the default value for the default language
export const formatTranslationMessages = (i18n, locale, messages, currentLanguage) => {
  const DEFAULT_LOCALE = currentLanguage;
  const defaultFormattedMessages = {};

  // Merge default messages and overrides
  const mergedFormattedMessages = Object.keys(messages).reduce((formattedMessages, key) => {
    const formattedMessage =
      !messages[key] && locale !== DEFAULT_LOCALE ? defaultFormattedMessages[key] : messages[key];

    const assignedMessages = Object.assign(formattedMessages, {
      [key]: formattedMessage,
    });

    if (
      locale === currentLanguage &&
      i18n.currentTranslations &&
      Object.prototype.hasOwnProperty.call(i18n.currentTranslations, key)
    ) {
      return Object.assign(assignedMessages, {
        [key]: i18n.currentTranslations[key],
      });
    }
    return assignedMessages;
  }, {});

  const additionalConfigMessages = Object.keys(i18n.currentTranslations).reduce(
    (configMessages, key) =>
      Object.assign(configMessages, { [key]: i18n.currentTranslations[key] }),
    {},
  );

  Object.assign(mergedFormattedMessages, additionalConfigMessages);

  // Load locale json files if required
  if (locale !== currentLanguage) {
    // !!!COMMENT-DLE, TODO-PTE: the following line seems not to work as loadLocaleOverride is making an axios async call
    // but we are returning before we actually receive the translations from S3. Please double check!!!
    loadLocaleOverride(i18n.translations, locale, mergedFormattedMessages);
  }

  return mergedFormattedMessages;
};

export const getTranslationMessages = (currentLanguage, labelOverrides) => ({
  en: formatTranslationMessages(labelOverrides, 'en', enTranslationMessages, currentLanguage),
  fr: formatTranslationMessages(labelOverrides, 'fr', frTranslationMessages, currentLanguage),
  de: formatTranslationMessages(labelOverrides, 'de', deTranslationMessages, currentLanguage),
  it: formatTranslationMessages(labelOverrides, 'it', itTranslationMessages, currentLanguage),
  es: formatTranslationMessages(labelOverrides, 'es', esTranslationMessages, currentLanguage),
  ca: formatTranslationMessages(labelOverrides, 'ca', caTranslationMessages, currentLanguage),
  pt: formatTranslationMessages(labelOverrides, 'pt', ptTranslationMessages, currentLanguage),
  eu: formatTranslationMessages(labelOverrides, 'eu', euTranslationMessages, currentLanguage),
  nl: formatTranslationMessages(labelOverrides, 'nl', nlTranslationMessages, currentLanguage),
  pl: formatTranslationMessages(labelOverrides, 'pl', enTranslationMessages, currentLanguage),
  sq: formatTranslationMessages(labelOverrides, 'sq', enTranslationMessages, currentLanguage),
  bs: formatTranslationMessages(labelOverrides, 'bs', enTranslationMessages, currentLanguage),
  hr: formatTranslationMessages(labelOverrides, 'hr', enTranslationMessages, currentLanguage),
  cs: formatTranslationMessages(labelOverrides, 'cs', enTranslationMessages, currentLanguage),
  fi: formatTranslationMessages(labelOverrides, 'fi', fiTranslationMessages, currentLanguage),
  hu: formatTranslationMessages(labelOverrides, 'hu', enTranslationMessages, currentLanguage),
  lt: formatTranslationMessages(labelOverrides, 'lt', enTranslationMessages, currentLanguage),
  no: formatTranslationMessages(labelOverrides, 'no', enTranslationMessages, currentLanguage),
  ro: formatTranslationMessages(labelOverrides, 'ro', enTranslationMessages, currentLanguage),
  ru: formatTranslationMessages(labelOverrides, 'ru', enTranslationMessages, currentLanguage),
  sk: formatTranslationMessages(labelOverrides, 'sk', enTranslationMessages, currentLanguage),
  sl: formatTranslationMessages(labelOverrides, 'sl', enTranslationMessages, currentLanguage),
  tr: formatTranslationMessages(labelOverrides, 'tr', enTranslationMessages, currentLanguage),
  uk: formatTranslationMessages(labelOverrides, 'uk', enTranslationMessages, currentLanguage),
  mk: formatTranslationMessages(labelOverrides, 'mk', enTranslationMessages, currentLanguage),
  ja: formatTranslationMessages(labelOverrides, 'ja', enTranslationMessages, currentLanguage),
  ar: formatTranslationMessages(labelOverrides, 'ar', enTranslationMessages, currentLanguage),
  da: formatTranslationMessages(labelOverrides, 'da', enTranslationMessages, currentLanguage),
  az: formatTranslationMessages(labelOverrides, 'az', enTranslationMessages, currentLanguage),
  zh: formatTranslationMessages(labelOverrides, 'zh', enTranslationMessages, currentLanguage),
  sr: formatTranslationMessages(labelOverrides, 'sr', enTranslationMessages, currentLanguage),
  el: formatTranslationMessages(labelOverrides, 'el', elTranslationMessages, currentLanguage),
});
